<script setup lang="ts">
/**
 * /job-application-tracker body: why keep track, how the Applications board
 * (pages/applications.vue) works, what each application keeps, and that it
 * is free. The board lives in the signed-in app, so the buttons open it or
 * the sign-up.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const startPath = computed(() => (isAuthenticated.value ? '/applications' : '/registration'))

const steps = [
  { id: 'add', icon: 'bi-plus-square' },
  { id: 'move', icon: 'bi-arrows-move' },
  { id: 'followUp', icon: 'bi-bell' },
  { id: 'learn', icon: 'bi-graph-up' },
] as const

const keeps = ['where', 'what', 'when', 'notes'] as const
const connected = ['tailor', 'letter', 'cvCard'] as const
</script>

<template>
  <!-- Why -->
  <section class="sofax surface-alt relative overflow-hidden">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <div class="eyebrow mb-5">{{ t('trackerPage.why.eyebrow') }}</div>
            <h2 class="mb-5 !text-3xl md:!text-[2.5rem]">{{ t('trackerPage.why.title') }}</h2>
            <p class="!mb-5 text-lg">{{ t('trackerPage.why.p1') }}</p>
            <p class="!mb-8">{{ t('trackerPage.why.p2') }}</p>
            <SofaxBtn :to="startPath" :label="t('trackerPage.cta')" variant="accent" />
          </div>
          <div class="mx-auto w-full max-w-[34rem]">
            <MarketingTrackerMockBoard />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- How it works -->
  <section class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <SofaxSectionHeading
          :eyebrow="t('trackerPage.how.eyebrow')"
          :title="t('trackerPage.how.title')"
          :text="t('trackerPage.how.subtitle')"
        />
        <ol class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <li
            v-for="(step, index) in steps"
            :key="step.id"
            class="h-full rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
          >
            <div class="flex items-center gap-4">
              <SofaxIconTile :icon="step.icon" size="sm" />
              <span class="font-title text-sm font-bold text-muted dark:text-gray-400">{{ index + 1 }} / {{ steps.length }}</span>
            </div>
            <div class="display-heading display-heading-5 mb-3 mt-6">{{ t(`trackerPage.how.steps.${step.id}.title`) }}</div>
            <p class="text-base">{{ t(`trackerPage.how.steps.${step.id}.text`) }}</p>
          </li>
        </ol>
      </div>
    </div>
  </section>

  <!-- What each application keeps, what it connects to, what it costs -->
  <section class="sofax surface-alt">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800 md:p-10">
            <SofaxIconTile icon="bi-kanban" round size="md" />
            <h2 class="mb-6 mt-8 !text-2xl !text-white md:!text-3xl">{{ t('trackerPage.keeps.title') }}</h2>
            <ul class="flex flex-col gap-4">
              <li v-for="item in keeps" :key="item" class="flex items-start gap-3">
                <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent-soft" />
                <span class="text-base !text-white/85">{{ t(`trackerPage.keeps.items.${item}`) }}</span>
              </li>
            </ul>
          </div>

          <div class="flex flex-col gap-6">
            <div class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-5">{{ t('trackerPage.connected.title') }}</div>
              <ul class="flex flex-col gap-3">
                <li v-for="item in connected" :key="item" class="flex items-start gap-3">
                  <i class="bi bi-link-45deg mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`trackerPage.connected.items.${item}`) }}</span>
                </li>
              </ul>
              <div class="mt-5">
                <SofaxArrowLink to="/tailor-resume-to-job" :label="t('trackerPage.connected.tailorLink')" />
              </div>
            </div>
            <div class="rounded-[0.625rem] border-2 border-accent bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-2">{{ t('trackerPage.free.title') }}</div>
              <p class="text-base">{{ t('trackerPage.free.text') }}</p>
            </div>
          </div>
        </div>

        <div class="mt-14 text-center">
          <SofaxBtn :to="startPath" :label="t('trackerPage.cta')" variant="dark" />
          <p class="mt-4 text-sm text-muted dark:text-gray-400">{{ t('trackerPage.ctaNote') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>
